/**
 * TherapyPlanTemplatesTab — Settings surface for Therapy Plan Templates
 * (iter-67f).
 *
 * Owner/Admin only. Templates are the reusable definition; per-student
 * Therapy Plans are auto-created by the backend when an invoice
 * generated from a template is marked paid.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, PencilSimple, Copy, Archive, ArrowCounterClockwise, Trash } from "@phosphor-icons/react";

const EMPTY_LINE = { therapy_type_id: "", sessions_per_week: 1, total_sessions: 4 };

// Stable per-row identity so React reconciles rows correctly when a line
// item is added or removed (index keys leak state into the wrong row).
let lineUidSeq = 0;
const newLine = (patch = {}) => ({ ...EMPTY_LINE, ...patch, _uid: `tpl-line-${++lineUidSeq}` });
const stampLine = (li) => ({ ...li, _uid: li._uid || `tpl-line-${++lineUidSeq}` });

const emptyForm = () => ({
  name: "",
  billing_period_type: "monthly", // "weekly" | "monthly"
  fee_mode: "per_session",        // "per_session" | "flat_period"
  fee_per_session: 800,
  flat_period_fee: 0,
  weeks_in_period: 1,             // relevant only for weekly
  line_items: [newLine()],
});

export default function TherapyPlanTemplatesTab() {
  const [items, setItems] = useState([]);
  const [therapyTypes, setTherapyTypes] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const status = showArchived ? "all" : "active";
      const [tpls, tt] = await Promise.all([
        api.get(`/therapy-plan-templates?status=${status}`),
        api.get("/therapy-types"),
      ]);
      setItems(tpls.data || []);
      setTherapyTypes((tt.data || []).filter(t => t.active !== false));
    } catch (e) {
      toast.error(formatApiError(e, "Could not load templates."));
    }
  };
  useEffect(() => { load(); }, [showArchived]);

  // Auto-compute total_sessions when the admin edits sessions_per_week
  // or weeks_in_period. Admin can still override total manually — we
  // only recompute if `_locked=false`. Setting a manual value locks it.
  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    setForm({
      name: tpl.name || "",
      billing_period_type: tpl.billing_period_type || "monthly",
      fee_mode: tpl.fee_mode || "per_session",
      fee_per_session: tpl.fee_per_session ?? 0,
      flat_period_fee: tpl.flat_period_fee ?? 0,
      weeks_in_period: tpl.weeks_in_period ?? 1,
      line_items: (tpl.line_items || []).map(stampLine),
    });
    setDialogOpen(true);
  };

  const defaultWeeks = (period, custom) =>
    period === "weekly" ? Math.max(1, Number(custom || 1)) : 4;

  const setLine = (idx, patch) => {
    setForm(f => {
      const next = f.line_items.map((li, i) => i === idx ? { ...li, ...patch } : li);
      // Auto-recompute total_sessions unless `total_sessions` was
      // explicitly patched (admin manually overriding).
      if (patch.sessions_per_week != null) {
        const wks = defaultWeeks(f.billing_period_type, f.weeks_in_period);
        next[idx].total_sessions = Math.max(0, Number(patch.sessions_per_week || 0) * wks);
      }
      return { ...f, line_items: next };
    });
  };

  const addLine = () => setForm(f => ({ ...f, line_items: [...f.line_items, newLine()] }));
  const removeLine = (idx) => setForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));

  const setPeriodType = (v) => {
    setForm(f => {
      // Recompute total_sessions across all lines using the new period.
      const wks = defaultWeeks(v, f.weeks_in_period);
      return {
        ...f,
        billing_period_type: v,
        line_items: f.line_items.map(li => ({
          ...li,
          total_sessions: Number(li.sessions_per_week || 0) * wks,
        })),
      };
    });
  };

  const setWeeks = (n) => {
    setForm(f => {
      const nn = Math.max(1, Number(n || 1));
      return {
        ...f,
        weeks_in_period: nn,
        line_items: f.line_items.map(li => ({
          ...li,
          total_sessions: Number(li.sessions_per_week || 0) * nn,
        })),
      };
    });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Template name is required.");
    if (!form.line_items.length) return toast.error("At least one therapy line item is required.");
    for (const li of form.line_items) {
      if (!li.therapy_type_id) return toast.error("Every line item needs a therapy type.");
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        billing_period_type: form.billing_period_type,
        fee_mode: form.fee_mode,
        fee_per_session: form.fee_mode === "per_session" ? Number(form.fee_per_session) : null,
        flat_period_fee: form.fee_mode === "flat_period" ? Number(form.flat_period_fee) : null,
        weeks_in_period: form.billing_period_type === "weekly" ? Number(form.weeks_in_period) : 1,
        line_items: form.line_items.map(li => ({
          therapy_type_id: li.therapy_type_id,
          sessions_per_week: Number(li.sessions_per_week || 0),
          total_sessions: Number(li.total_sessions || 0),
        })),
      };
      if (editing) {
        await api.put(`/therapy-plan-templates/${editing.id}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/therapy-plan-templates", payload);
        toast.success("Template created");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async (tpl) => {
    try {
      await api.post(`/therapy-plan-templates/${tpl.id}/duplicate`);
      toast.success(`Duplicated "${tpl.name}"`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleArchive = async (tpl) => {
    try {
      await api.post(`/therapy-plan-templates/${tpl.id}/toggle-archive`);
      toast.success(tpl.status === "active" ? "Archived" : "Restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const ttName = useMemo(() => {
    const m = {};
    therapyTypes.forEach(t => { m[t.id] = t.name; });
    return (id) => m[id] || "—";
  }, [therapyTypes]);

  return (
    <Card data-testid="therapy-plan-templates-tab">
      <CardHeader className="flex-row items-center justify-between">
        <div>
          <CardTitle>Therapy Plan Templates</CardTitle>
          <div className="text-xs text-muted-foreground mt-1">
            Reusable plan definitions. Selecting one in the Invoice dialog
            pre-fills the line items and fee. Owner and Admin only.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={e => setShowArchived(e.target.checked)}
              data-testid="tpl-show-archived"
            />
            Show archived
          </label>
          <Button size="sm" onClick={openNew} data-testid="tpl-new-btn">
            <Plus className="w-4 h-4 mr-1" /> New template
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!items.length ? (
          <div className="text-sm text-muted-foreground italic p-4 text-center">
            No templates yet. Click <strong>New template</strong> to add one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2">Name</th>
                <th className="py-2">Period</th>
                <th className="py-2">Fee</th>
                <th className="py-2">Line items</th>
                <th className="py-2">Status</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(t => (
                <tr key={t.id} className="border-b hover:bg-muted/30" data-testid={`tpl-row-${t.id}`}>
                  <td className="py-2 font-medium">{t.name}</td>
                  <td className="py-2 capitalize">
                    {t.billing_period_type}
                    {t.billing_period_type === "weekly" && ` × ${t.weeks_in_period || 1}`}
                  </td>
                  <td className="py-2">
                    {t.fee_mode === "per_session"
                      ? <>₹{t.fee_per_session ?? 0} / session</>
                      : <>₹{t.flat_period_fee ?? 0} flat</>}
                  </td>
                  <td className="py-2">
                    {(t.line_items || []).map((li, i) => (
                      <Badge key={`${t.id}-${li.therapy_type_id || "x"}-${i}`} variant="outline" className="mr-1 text-[10px]">
                        {ttName(li.therapy_type_id)} · {li.sessions_per_week}/wk · {li.total_sessions} total
                      </Badge>
                    ))}
                  </td>
                  <td className="py-2">
                    <Badge variant={t.status === "active" ? "default" : "secondary"}>{t.status}</Badge>
                  </td>
                  <td className="py-2 text-right space-x-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(t)} data-testid={`tpl-edit-${t.id}`}>
                      <PencilSimple className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => duplicate(t)} data-testid={`tpl-duplicate-${t.id}`}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => toggleArchive(t)} data-testid={`tpl-toggle-archive-${t.id}`}>
                      {t.status === "active" ? <Archive className="w-4 h-4" /> : <ArrowCounterClockwise className="w-4 h-4" />}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit template" : "New template"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Template name *</Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Premium Autism Support Monthly"
                data-testid="tpl-form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Billing period</Label>
                <Select value={form.billing_period_type} onValueChange={setPeriodType}>
                  <SelectTrigger data-testid="tpl-form-period"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.billing_period_type === "weekly" && (
                <div>
                  <Label className="text-xs">Weeks in period</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.weeks_in_period}
                    onChange={e => setWeeks(e.target.value)}
                    data-testid="tpl-form-weeks"
                  />
                </div>
              )}
              <div>
                <Label className="text-xs">Fee mode</Label>
                <Select value={form.fee_mode} onValueChange={v => setForm({ ...form, fee_mode: v })}>
                  <SelectTrigger data-testid="tpl-form-fee-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_session">Per session</SelectItem>
                    <SelectItem value="flat_period">Flat period fee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">
                  {form.fee_mode === "per_session" ? "Fee per session (₹)" : "Flat period fee (₹)"}
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={form.fee_mode === "per_session" ? form.fee_per_session : form.flat_period_fee}
                  onChange={e => {
                    const n = Number(e.target.value);
                    setForm(f => form.fee_mode === "per_session"
                      ? { ...f, fee_per_session: n } : { ...f, flat_period_fee: n });
                  }}
                  data-testid="tpl-form-fee-value"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Therapy line items</Label>
                <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="tpl-form-add-line">
                  <Plus className="w-3 h-3 mr-1" /> Add line
                </Button>
              </div>
              <div className="space-y-2">
                {form.line_items.map((li, idx) => (
                  <div
                    key={li._uid || `tpl-line-idx-${idx}`}
                    className="grid grid-cols-12 gap-2 items-end p-2 border rounded-md"
                    data-testid={`tpl-form-line-${idx}`}
                  >
                    <div className="col-span-5">
                      <Label className="text-[10px]">Therapy type</Label>
                      <Select value={li.therapy_type_id} onValueChange={v => setLine(idx, { therapy_type_id: v })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent>
                          {therapyTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px]">Sessions / week</Label>
                      <Input
                        type="number"
                        min={0}
                        value={li.sessions_per_week}
                        onChange={e => setLine(idx, { sessions_per_week: Number(e.target.value) })}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px]">Total sessions</Label>
                      <Input
                        type="number"
                        min={0}
                        value={li.total_sessions}
                        onChange={e => setLine(idx, { total_sessions: Number(e.target.value) })}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="col-span-1">
                      <Button size="icon" variant="ghost" onClick={() => removeLine(idx)}>
                        <Trash className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="tpl-form-save">
              {saving ? "Saving…" : (editing ? "Save changes" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
